/** @jsxImportSource @emotion/react */
"use client";

import type { PropsWithChildren } from "react";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useState,
} from "react";

import type { TransitionKind } from "../../components/blocks/other/Transition/Transition.emotion";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../../lib/constants/endOfBrowserAsyncQueue.constants";
import type { WithAsChild } from "../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../utils/determineElementFromAsChild";

export type TransitionContextShape = {
  entering: string;
  entered: string;
  exiting: string;
  exited: string;
  transitionMs: number;
  transitionInDelay: number;
  transitionOutDelay: number;
} & TransitionKind;

export const TransitionContext = createContext<TransitionContextShape>({
  entering: "",
  entered: "",
  exiting: "",
  exited: "",
  transitionMs: 250,
  transitionInDelay: 0,
  transitionOutDelay: 0,
  kind: "fade",
});

export const useTransition = () => useContext(TransitionContext);

export type TransitionProviderProps = WithAsChild<
  PropsWithChildren<
    {
      defaultValue: string;
      transitionMs?: number;
      transitionDelay?: number;
      transitionInDelay?: number;
      transitionOutDelay?: number;
      value: string;
      className?: string;
    } & TransitionKind
  >
>;

const hallowElement = "div";

export const TransitionRoot = forwardRef<
  HTMLDivElement,
  TransitionProviderProps
>(
  (
    {
      children,
      defaultValue,
      value,
      transitionMs = 250,
      transitionDelay = 0,
      transitionInDelay = transitionDelay,
      transitionOutDelay = transitionDelay,
      asChild,
      className = "",
      ...kindProps
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const [entering, setEntering] = useState<string>("");
    const [entered, setEntered] = useState<string>(defaultValue);
    const [exiting, setExiting] = useState<string>("");
    const [exited, setExited] = useState<string>("");

    useEffect(() => {
      const render = async () => {
        setExiting(entered);
        setEntering(value);
        setEntered("");
        setExited("");

        setTimeout(
          () => {
            requestAnimationFrame(() => {
              setExited(entered);
              setEntered(value);
              setEntering("");
              setExiting("");
              setTimeout(() => {
                setExited("");
              }, transitionMs);
            });
          },
          Math.max(
            transitionInDelay,
            transitionOutDelay,
            END_OF_BROWSER_ASYNC_QUEUE,
          ),
        );
      };

      if (value !== entered) {
        render();
      }
    }, [value]);

    return (
      <TransitionContext.Provider
        value={{
          entering,
          entered,
          exiting,
          exited,
          transitionMs,
          transitionInDelay,
          transitionOutDelay,
          ...kindProps,
        }}
      >
        <DeterminedElement
          ref={ref}
          css={{
            position: "relative",
          }}
          className={className}
        >
          {children}
        </DeterminedElement>
      </TransitionContext.Provider>
    );
  },
);

TransitionRoot.displayName = "TransitionRoot";
